// Graph model for the TIL digital garden.
//
// Reverse-engineered from Quartz's graph view: notes become nodes connected by
// their wikilinks, with tags promoted to their own nodes so notes that share a
// tag cluster together. Everything is derived from the manifest (the same data
// the lists and backlinks use) — no extra fetches.
//
// Edge reconstruction note: the manifest only stores each note's `backlinks`
// (slugs that link *to* it). Since a backlink `b` on note `X` means `b → X`,
// iterating every note's backlinks recovers the complete link graph.

const TAG_PREFIX = "tag:";

export function tagNodeId(tag) {
  return `${TAG_PREFIX}${tag}`;
}

export function isTagNode(node) {
  return node.type === "tag";
}

// Memoized full graph, keyed by the (stable, cached) manifest array so the
// homepage card and the expand-modal share one build instead of each recomputing
// it. GraphView clones nodes/links before the simulation mutates them, so handing
// the same object to several renderers is safe. (LocalGraph keeps its own
// buildGraph: localGraph() rewrites node.degree in place on the subgraph.)
const fullGraphCache = new WeakMap();
export function fullGraph(notes) {
  let graph = fullGraphCache.get(notes);
  if (!graph) {
    graph = buildGraph(notes);
    fullGraphCache.set(notes, graph);
  }
  return graph;
}

// Build the full graph from the manifest notes array.
// Returns { nodes, links } where nodes carry a `degree` (neighbor count) so the
// renderer can size them — bigger = more connected, like Quartz.
export function buildGraph(notes) {
  const noteSlugs = new Set(notes.map((note) => note.slug));
  const nodes = new Map();
  const links = new Map(); // key: "a b" (sorted) -> { source, target }

  const addNode = (id, data) => {
    if (!nodes.has(id)) nodes.set(id, { id, ...data });
    return nodes.get(id);
  };

  const addLink = (a, b) => {
    if (a === b) return;
    const key = a < b ? `${a} ${b}` : `${b} ${a}`;
    if (!links.has(key)) links.set(key, { source: a, target: b });
  };

  for (const note of notes) {
    addNode(note.slug, {
      type: "note",
      title: note.title ?? note.slug,
      tags: note.tags ?? [],
    });

    // Link edges, recovered from backlinks (b -> note.slug). Guard against
    // backlinks pointing at notes that aren't published in this manifest.
    for (const from of note.backlinks ?? []) {
      if (noteSlugs.has(from)) addLink(from, note.slug);
    }

    // Tag nodes + note<->tag edges.
    for (const tag of note.tags ?? []) {
      const id = tagNodeId(tag);
      addNode(id, { type: "tag", label: tag });
      addLink(note.slug, id);
    }
  }

  const nodeList = [...nodes.values()];
  const linkList = [...links.values()];
  applyDegrees(nodeList, linkList);

  return { nodes: nodeList, links: linkList };
}

// Induced subgraph around a focus note: the focus, plus everything reachable
// within `depth` hops over the combined note+tag graph (depth 1 = the note, its
// tags, and directly linked notes). The focus node is flagged with `focus: true`.
export function localGraph(graph, focusId, depth = 1) {
  if (!graph.nodes.some((node) => node.id === focusId)) {
    return { nodes: [], links: [] };
  }

  const adjacency = new Map();
  for (const link of graph.links) {
    const a = linkEndId(link.source);
    const b = linkEndId(link.target);
    if (!adjacency.has(a)) adjacency.set(a, new Set());
    if (!adjacency.has(b)) adjacency.set(b, new Set());
    adjacency.get(a).add(b);
    adjacency.get(b).add(a);
  }

  const keep = new Set([focusId]);
  let frontier = [focusId];
  for (let hop = 0; hop < depth; hop++) {
    const next = [];
    for (const id of frontier) {
      for (const neighbor of adjacency.get(id) ?? []) {
        if (!keep.has(neighbor)) {
          keep.add(neighbor);
          next.push(neighbor);
        }
      }
    }
    frontier = next;
  }

  const byId = new Map(graph.nodes.map((node) => [node.id, node]));
  const nodes = [...keep]
    .map((id) => byId.get(id))
    .filter(Boolean)
    .map((node) => (node.id === focusId ? { ...node, focus: true } : node));
  const links = graph.links.filter(
    (link) => keep.has(linkEndId(link.source)) && keep.has(linkEndId(link.target))
  );

  // Fresh, subgraph-local degrees so node sizing reflects the local view.
  applyDegrees(nodes, links);

  return { nodes, links };
}

// d3-force mutates link.source/target from id strings into node objects once the
// simulation starts; accept either form.
function linkEndId(end) {
  return typeof end === "object" && end !== null ? end.id : end;
}

function applyDegrees(nodes, links) {
  const degree = new Map();
  for (const link of links) {
    const a = linkEndId(link.source);
    const b = linkEndId(link.target);
    degree.set(a, (degree.get(a) ?? 0) + 1);
    degree.set(b, (degree.get(b) ?? 0) + 1);
  }
  for (const node of nodes) node.degree = degree.get(node.id) ?? 0;
}
